import { Modal as AModal, Card, Col, Row, Spin } from 'antd';

import LoadingIndicator from 'components/LoadingIndicator';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { Viewport } from 'components/ViewportProvider';
import messages from 'containers/OrdersPage/messages';
import moment from 'moment';
import React, { lazy, Suspense } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import translations from 'translations';
import { Order } from 'types/schema';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';
import DesktopTable from './DesktopTable';
import InfoIcon from './Info.svg';
import MobileTable from './MobileTable';
import OrderSummary, { MoneySection } from './OrderSummary';

const LazyModal = lazy(() => import('antd').then((module) => ({ default: module.Modal })));

const Container = styled.section``;

const OrderRow = styled(Row)`
  margin-bottom: 15px;
`;

const OrderCol = styled(Col)`
  padding: 16px;
`;
const TableContainer = styled.div`
  .ant-table-body table {
    display: flex;
    flex-direction: column;
    th {
      text-transform: capitalize;
    }
  }
  .ant-table-body .ant-table-row {
    display: block;
    width: 100%;
  }
  .ant-table-row td {
    display: block;
    width: 100%;
  }
  ${media.sm`
    .ant-table-body table {
      display: table;
      flex-direction: column;
    }
    .ant-table-body .ant-table-row {
      display: table-row;
    }
    .ant-table-row td {
      display: table-cell;
      width: 0;
    }
  `};
`;
const OrderCard = styled.div`
  border-radius: 8px;
  margin-bottom: 16px;
  background-color: #fff;
  .ant-card-body {
    padding: 0 !important;
  }
`;
export const OrderInfoSection = styled.div<{ isMobileSize?: boolean }>`
  padding: 12px;
  display: flex;
  gap: 6px;
  align-items: ${({ isMobileSize }) => (isMobileSize ? 'flex-start' : 'center')};
  border-radius: 6px;
  background-color: #fffbe6;
  margin-bottom: 16px;
  ${({ isMobileSize }) => isMobileSize && `margin: 8px 12px`};
`;
export const Icon = styled(SVGIcon)`
  width: 20px;
  height: 20px;
`;

interface OrderProps {
  order: any;
  isStatusChange?: boolean;
  onCancelOrder: (value: any) => void;
  onApproveOrder: (value: any) => void;
  onDisapproveOrder: (value: any) => void;
  onConfirmOrder: (value: any) => void;
  onReOrderAll: (value: any) => void;
  onUpdateDelivery: (value: any) => void;
  onPrintOrderInvoice: (value: string) => void;
  intl: any;
  permissions: string[];
}

class OrderDetails extends React.Component<OrderProps, any> {
  public onCancelOrder = () => {
    const cancelOrderMessage = this.props.intl.formatMessage(messages.cancelOrderMessage);
    const yes = this.props.intl.formatMessage(utilsMessages.yes);
    const no = this.props.intl.formatMessage(utilsMessages.no);
    AModal.confirm({
      autoFocusButton: null,
      okButtonProps: {
        danger: true,
      },
      title: cancelOrderMessage,
      okText: yes,
      cancelText: no,
      onOk: () => {
        const { order } = this.props;
        this.props.onCancelOrder({
          id: order?.id,
        });
      },
    });
  };
  public onDisapproveOrder = () => {
    const declineOrderMessage = translations(messages.declineOrderMessage);
    const yes = translations(utilsMessages.yes);
    const no = translations(utilsMessages.no);
    AModal.confirm({
      autoFocusButton: null,
      okButtonProps: {
        danger: true,
      },
      title: declineOrderMessage,
      okText: yes,
      cancelText: no,
      onOk: () => {
        const { order } = this.props;
        this.props.onDisapproveOrder({
          orderId: order?.id,
        });
      },
    });
  };

  public onApproveOrder = () => {
    const { order } = this.props;
    this.props.onApproveOrder({
      orderId: order?.id,
    });
  };

  public onConfirmOrder = () => {
    const { order } = this.props;
    if (moment(order?.deliveryTime.start).isAfter(new Date())) {
      const yes = translations(utilsMessages.yes);
      const no = translations(utilsMessages.no);
      AModal.confirm({
        autoFocusButton: null,
        title: translations(messages.confirmDeliveryOrderMessage),
        okText: yes,
        cancelText: no,
        onOk: () => {
          const { order } = this.props;
          this.props.onConfirmOrder({
            id: order?.id,
          });
        },
      });
    } else {
      this.props.onConfirmOrder({
        id: order?.id,
      });
    }
  };

  public reOrderAll = () => {
    const { order } = this.props;
    this.props.onReOrderAll({
      storeId: order?.store.id,
      orderId: order?.id,
    });
  };

  public render() {
    const { order, permissions, isStatusChange } = this.props;
    const isHaveSomeKgUom = order.items.some(
      (item: Order['items'][0]) => item?.product?.uomLocal?.toLowerCase() === 'kg',
    );

    return (
      <>
        <Viewport.Consumer>
          {({ width }) => {
            const isMobileSize = width < 768;
            return (
              <Spin spinning={order?.loading}>
                <Container>
                  <OrderCard bordered={false}>
                    <OrderSummary
                      order={order}
                      onCancelOrder={this.onCancelOrder}
                      reOrderAll={this.reOrderAll}
                      onDisapproveOrder={this.onDisapproveOrder}
                      onApproveOrder={this.onApproveOrder}
                      onPrintOrderInvoice={this.props.onPrintOrderInvoice}
                      isMobileSize={isMobileSize}
                      onUpdateDelivery={this.props.onUpdateDelivery}
                      permissions={permissions}
                      showInfoTooltip={isHaveSomeKgUom}
                    />
                  </OrderCard>

                  {!isMobileSize && order.fulfillmentNote && order.fulfillmentNote?.orderIds?.length > 1 ? (
                    <div style={{ margin: '16px 0' }}>
                      <OrderInfoSection>
                        <Icon src={InfoIcon} />
                        <span>
                          <FormattedMessage
                            {...messages.orderFFMNoteInfo}
                            values={{
                              fulfillmentNote: `${order.fulfillmentNote?.fulfillmentNoteShortCode}`,
                              orderIds: `(${order.fulfillmentNote?.orderIds
                                ?.map((id: string) => `${id.substring(0, 5)}...`)
                                .join(', ')})`,
                            }}
                          />{' '}
                          <Link to={`/phieu-giao/${order.fulfillmentNote.fulfillmentNoteId}`}>
                            <FormattedMessage {...messages.seeHere} />
                          </Link>
                        </span>
                      </OrderInfoSection>
                    </div>
                  ) : null}

                  <Card bordered={false} style={{ borderBottomRightRadius: 0 }} bodyStyle={{ padding: 0 }}>
                    {isMobileSize ? (
                      <>
                        <TableContainer>
                          <MobileTable order={order} />
                        </TableContainer>

                        <OrderRow gutter={8}>
                          <OrderCol lg={24} sm={24} xs={24} style={{ paddingBottom: 0 }}>
                            <MoneySection isMobileSize={isMobileSize} order={order} hideInfoText={!isHaveSomeKgUom} />
                          </OrderCol>
                        </OrderRow>
                      </>
                    ) : (
                      <DesktopTable order={order} />
                    )}
                  </Card>
                </Container>
              </Spin>
            );
          }}
        </Viewport.Consumer>

        {isStatusChange && (
          <Suspense fallback={<LoadingIndicator />}>
            <LazyModal
              centered
              style={{ textAlign: 'center', maxWidth: '380px' }}
              open={isStatusChange}
              onOk={() => {
                window.location.reload();
              }}
              okText={translations(utilsMessages.confirm)}
              cancelButtonProps={{ style: { display: 'none' } }}
              closable={false}
              okButtonProps={{ style: { marginLeft: 'auto', marginRight: 'auto', display: 'block' } }}
            >
              {translations(messages.reloadOrderText)}
            </LazyModal>
          </Suspense>
        )}
      </>
    );
  }
}

export default injectIntl(OrderDetails);
